import React, { useState, useRef, useEffect } from "react";
import useSWR from "swr";
import api from "@/lib/api";
import { ddmmyyyy } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Gavel, CheckCircle2, XCircle, Clock, FileSignature, Ban, PenLine, Eraser, Loader2, AlertTriangle,
} from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

const STATE_META = {
  pending_review: { label: "Awaiting review", colour: "bg-amber-100 text-amber-800 border-amber-200",   icon: Clock },
  disputed:       { label: "You disputed",    colour: "bg-purple-100 text-purple-800 border-purple-200", icon: FileSignature },
  approved:       { label: "Approved",        colour: "bg-blue-100 text-blue-800 border-blue-200",       icon: CheckCircle2 },
  applied:        { label: "Applied to pay",  colour: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  rejected:       { label: "Not charged",     colour: "bg-gray-100 text-gray-600 border-gray-200",       icon: XCircle },
  cancelled:      { label: "Withdrawn",       colour: "bg-gray-100 text-gray-500 border-gray-200",       icon: Ban },
};

const StateBadge = ({ state }) => {
  const m = STATE_META[state] || STATE_META.pending_review;
  const I = m.icon;
  return (
    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${m.colour}`}
           data-testid={`penalty-state-${state}`}>
      <I className="w-3 h-3 mr-1" /> {m.label}
    </Badge>
  );
};

const gbp = (n) => (typeof n === "number" ? `£${n.toFixed(2)}` : "—");

const DisputeDialog = ({ open, onOpenChange, penalty, onSubmitted }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [evidenceFile, setEvidenceFile] = useState(null);

  useEffect(() => {
    if (!open) { setReason(""); return; }
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#0F172A";
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height);
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: (t.clientX - r.left) * (c.width / r.width),
               y: (t.clientY - r.top) * (c.height / r.height) };
    };
    const down = (e) => { e.preventDefault(); drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
    const up = () => { drawing.current = false; };
    c.addEventListener("mousedown", down); c.addEventListener("mousemove", move);
    c.addEventListener("mouseup", up); c.addEventListener("mouseleave", up);
    c.addEventListener("touchstart", down, { passive: false });
    c.addEventListener("touchmove", move, { passive: false });
    c.addEventListener("touchend", up);
    return () => {
      c.removeEventListener("mousedown", down); c.removeEventListener("mousemove", move);
      c.removeEventListener("mouseup", up); c.removeEventListener("mouseleave", up);
      c.removeEventListener("touchstart", down); c.removeEventListener("touchmove", move);
      c.removeEventListener("touchend", up);
    };
  }, [open]);

  const clearCanvas = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height);
  };

  const isCanvasBlank = () => {
    const c = canvasRef.current; if (!c) return true;
    const ctx = c.getContext("2d");
    const d = ctx.getImageData(0, 0, c.width, c.height).data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i] < 250 || d[i + 1] < 250 || d[i + 2] < 250) return false;
    }
    return true;
  };

  const submit = async () => {
    if (reason.trim().length < 10) {
      toast.error("Please explain your dispute in at least 10 characters");
      return;
    }
    if (isCanvasBlank()) {
      toast.error("Please sign your statement");
      return;
    }
    setBusy(true);
    try {
      const sig = canvasRef.current.toDataURL("image/png");
      await api.post(`/penalties/${penalty.id}/dispute`, {
        reason: reason.trim(),
        signature_png_b64: sig,
      });
      if (evidenceFile) {
        const fd = new FormData(); fd.append("file", evidenceFile);
        await api.post(`/penalties/${penalty.id}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Dispute submitted — the admin will review your evidence.");
      onOpenChange(false);
      onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispute failed");
    }
    setBusy(false);
  };

  if (!penalty) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dispute-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-purple-600" />
            Dispute this penalty
          </DialogTitle>
          <DialogDescription>
            You are about to formally dispute penalty <b>#{penalty.id.slice(0, 8)}</b>{" "}
            ({penalty.penalty_type?.replace(/_/g, " ")} · {gbp(penalty.amount)}).
            The admin will review your evidence before any payroll deduction is applied.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">Explain your position</Label>
            <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g. This damage was already on the vehicle at handover — see photo audit ID PA-2026-001."
                      data-testid="dispute-reason" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Photo / WhatsApp evidence (optional)</Label>
            <input className="mt-1.5 block w-full text-xs" type="file" accept="image/*,application/pdf"
                   onChange={(e) => setEvidenceFile(e.target.files?.[0] || null)} />
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <PenLine className="w-3.5 h-3.5 text-brand-red" />
              <Label className="text-xs uppercase tracking-wider m-0">Sign your statement</Label>
            </div>
            <div className="relative border border-brand-border rounded-lg overflow-hidden bg-white">
              <canvas ref={canvasRef} width={800} height={200}
                       className="w-full h-32 touch-none cursor-crosshair"
                       data-testid="dispute-canvas" />
              <button type="button" onClick={clearCanvas}
                       className="absolute top-2 right-2 text-xs bg-white/90 border border-brand-border rounded px-2 py-1 hover:bg-brand-surface flex items-center gap-1"
                       data-testid="dispute-clear">
                <Eraser className="w-3 h-3" /> Clear
              </button>
            </div>
            <p className="mt-1.5 text-[11px] text-brand-text-muted">
              Your signature, IP address and device details are recorded as evidence.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
                   className="bg-purple-600 hover:bg-purple-700 text-white"
                   data-testid="dispute-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileSignature className="w-4 h-4 mr-1.5" />}
            Submit signed dispute
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PenaltyRow = ({ p, canDispute, onDispute }) => (
  <Card className="p-4 rounded-2xl border-brand-border" data-testid={`driver-penalty-${p.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Gavel className="w-4 h-4 text-brand-red" />
          <span className="text-xs font-medium">
            {(p.penalty_type || "").replace(/_/g, " ")}
          </span>
          <StateBadge state={p.state} />
        </div>
        <div className="font-heading text-lg font-bold">{gbp(p.amount)}</div>
        <div className="text-xs text-brand-text-muted mt-1 whitespace-pre-wrap">{p.reason}</div>
        <div className="text-[10px] font-mono text-brand-text-muted mt-2">
          Raised {ddmmyyyy(p.created_at?.slice(0, 10))}
          {p.incident_date ? ` · Incident ${ddmmyyyy(p.incident_date)}` : ""}
        </div>
        {p.driver_dispute && (
          <div className="mt-2 text-[11px] text-purple-800 bg-purple-50 border border-purple-200 rounded-lg p-2">
            Your dispute is with the admin. Submitted{" "}
            {p.driver_dispute.submitted_at?.slice(0, 10)}.
          </div>
        )}
        {p.state === "applied" && (
          <div className="mt-2 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-2">
            This has been applied to your pay period. See your Invoice for details.
          </div>
        )}
        {p.state === "rejected" && (
          <div className="mt-2 text-[11px] text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-2">
            The admin rejected this penalty — no deduction was made.
          </div>
        )}
      </div>
      {canDispute && !p.driver_dispute && (
        <Button size="sm" onClick={() => onDispute(p)}
                 className="bg-purple-600 hover:bg-purple-700 text-white"
                 data-testid={`dispute-btn-${p.id}`}>
          <FileSignature className="w-3.5 h-3.5 mr-1.5" /> Dispute
        </Button>
      )}
    </div>
  </Card>
);

const DriverPenalties = () => {
  const { data: penalties, mutate: reload } = useSWR("/penalties/mine", fetcher);
  const [disputing, setDisputing] = useState(null);

  const list = penalties || [];
  const open = list.filter((p) => p.state === "pending_review" || p.state === "approved");
  const history = list.filter((p) => !(p.state === "pending_review" || p.state === "approved"));

  return (
    <div className="p-4 space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Charges</div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-brand-text mt-1">Penalties &amp; disputes</h1>
        <p className="text-sm text-brand-text-muted mt-1">
          If you disagree with a charge, submit a signed dispute — the admin must review it
          before any money is taken from your pay.
        </p>
      </div>

      <section>
        <div className="text-xs uppercase tracking-wider text-brand-text-muted mb-2 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> Open ({open.length})
        </div>
        <div className="space-y-2" data-testid="open-penalties">
          {open.length === 0 && (
            <Card className="p-6 text-center text-sm text-brand-text-muted border-brand-border">
              You have no open penalties. 🎉
            </Card>
          )}
          {open.map((p) => <PenaltyRow key={p.id} p={p} canDispute={true} onDispute={setDisputing} />)}
        </div>
      </section>

      {history.length > 0 && (
        <section>
          <div className="text-xs uppercase tracking-wider text-brand-text-muted mb-2">
            History ({history.length})
          </div>
          <div className="space-y-2" data-testid="history-penalties">
            {history.map((p) => <PenaltyRow key={p.id} p={p} canDispute={false} onDispute={setDisputing} />)}
          </div>
        </section>
      )}

      <DisputeDialog open={!!disputing}
                     onOpenChange={(o) => { if (!o) setDisputing(null); }}
                     penalty={disputing}
                     onSubmitted={reload} />
    </div>
  );
};

export default DriverPenalties;
